// ======================================
// Admin Dashboard Module
// ======================================

import { icons } from '../../components.js';
import { requireAuth, getUser, logout } from '../../lib/auth.js';
import { dashboardAPI, leadsAPI } from '../../lib/api.js';
import { renderAdminLayout, initAdminLayout } from './admin-layout.js';

export function renderAdminDashboard() {
  // Require admin/manager role
  if (!requireAuth(['admin', 'manager', 'designer'])) {
    return '<div class="loading">Redirecting...</div>';
  }

  const user = getUser();

  const content = `
          <!-- Stats Cards -->
          <div class="admin-stats" id="admin-stats">
            <div class="stat-card stat-card-primary">
              <div class="stat-icon">${icons.users || '👥'}</div>
              <div class="stat-content">
                <span class="stat-value" id="stat-leads">-</span>
                <span class="stat-label">New Leads</span>
              </div>
            </div>
            <div class="stat-card stat-card-success">
              <div class="stat-icon">${icons.briefcase || '💼'}</div>
              <div class="stat-content">
                <span class="stat-value" id="stat-projects">-</span>
                <span class="stat-label">Active Projects</span>
              </div>
            </div>
            <div class="stat-card stat-card-warning">
              <div class="stat-icon">${icons.mail || '✉️'}</div>
              <div class="stat-content">
                <span class="stat-value" id="stat-inquiries">-</span>
                <span class="stat-label">Pending Inquiries</span>
              </div>
            </div>
            <div class="stat-card stat-card-info">
              <div class="stat-icon">${icons.calculator || '💰'}</div>
              <div class="stat-content">
                <span class="stat-value" id="stat-revenue">-</span>
                <span class="stat-label">Revenue (This Month)</span>
              </div>
            </div>
          </div>
          
          <!-- Recent Activity -->
          <div class="admin-grid">
            <div class="admin-card">
              <div class="admin-card-header">
                <h2>Recent Leads</h2>
                <a href="/admin/leads" class="btn btn-sm btn-outline">View All</a>
              </div>
              <div class="admin-card-body" id="recent-leads">
                <div class="loading-placeholder">Loading...</div>
              </div>
            </div>
            
            <div class="admin-card">
              <div class="admin-card-header">
                <h2>Recent Projects</h2>
                <a href="/admin/projects" class="btn btn-sm btn-outline">View All</a>
              </div>
              <div class="admin-card-body" id="recent-projects">
                <div class="loading-placeholder">Loading...</div>
              </div>
            </div>
          </div>
          
          <!-- Quick Actions -->
          <div class="admin-card">
            <div class="admin-card-header">
              <h2>Quick Actions</h2>
            </div>
            <div class="admin-card-body">
              <div class="quick-action-grid">
                <a href="/admin/leads" class="quick-action-btn">
                  <span class="quick-action-icon">${icons.plus || '+'}</span>
                  <span>View Leads</span>
                </a>
                <a href="/admin/projects" class="quick-action-btn">
                  <span class="quick-action-icon">${icons.briefcase || '💼'}</span>
                  <span>Manage Projects</span>
                </a>
                <a href="/estimator" class="quick-action-btn" target="_blank">
                  <span class="quick-action-icon">${icons.calculator || '🧮'}</span>
                  <span>Cost Estimator</span>
                </a>
                <a href="/contact" class="quick-action-btn" target="_blank">
                  <span class="quick-action-icon">${icons.mail || '✉️'}</span>
                  <span>Contact Page</span>
                </a>
              </div>
            </div>
          </div>
  `;

  return renderAdminLayout('dashboard', 'Dashboard', content);
}

export async function initAdminDashboard() {
  initAdminLayout();

  // Load dashboard data
  try {
    await loadDashboardStats();
    await loadRecentLeads();
    await loadRecentProjects();
  } catch (error) {
    console.error('Failed to load dashboard data:', error);
  }
}

async function loadDashboardStats() {
  try {
    const response = await dashboardAPI.stats();
    if (response.success && response.data) {
      const stats = response.data;

      document.getElementById('stat-leads').textContent = stats.new_leads || 0;
      document.getElementById('stat-projects').textContent = stats.active_projects || 0;
      document.getElementById('stat-inquiries').textContent = stats.pending_inquiries || 0;
      document.getElementById('stat-revenue').textContent = formatCurrency(stats.revenue_this_month || 0);

      // Update leads badge
      const leadsBadge = document.getElementById('leads-count');
      if (leadsBadge) {
        leadsBadge.textContent = stats.new_leads || 0;
      }
    }
  } catch (error) {
    console.error('Failed to load stats:', error);
  }
}

async function loadRecentLeads() {
  const container = document.getElementById('recent-leads');
  if (!container) return;

  try {
    const response = await dashboardAPI.recentLeads();
    if (response.success && response.data?.length > 0) {
      container.innerHTML = response.data.slice(0, 5).map(lead => `
        <div class="activity-item">
          <div class="activity-avatar">${lead.name.charAt(0).toUpperCase()}</div>
          <div class="activity-content">
            <p class="activity-name">${lead.name}</p>
            <p class="activity-meta">${lead.phone} • ${lead.property_type || 'N/A'}</p>
          </div>
          <span class="status-badge status-${lead.status}">${lead.status}</span>
        </div>
      `).join('');
    } else {
      container.innerHTML = '<p class="empty-state">No leads yet</p>';
    }
  } catch (error) {
    container.innerHTML = '<p class="error-state">Failed to load leads</p>';
  }
}

async function loadRecentProjects() {
  const container = document.getElementById('recent-projects');
  if (!container) return;

  try {
    const response = await dashboardAPI.recentProjects();
    if (response.success && response.data?.length > 0) {
      container.innerHTML = response.data.slice(0, 5).map(project => `
        <div class="activity-item">
          <div class="activity-avatar">${project.customer_name?.charAt(0).toUpperCase() || 'P'}</div>
          <div class="activity-content">
            <p class="activity-name">${project.title}</p>
            <p class="activity-meta">${project.customer_name || 'Unknown'} • ${formatCurrency(project.total_amount)}</p>
          </div>
          <span class="status-badge status-${project.status}">${project.status}</span>
        </div>
      `).join('');
    } else {
      container.innerHTML = '<p class="empty-state">No projects yet</p>';
    }
  } catch (error) {
    container.innerHTML = '<p class="error-state">Failed to load projects</p>';
  }
}

function formatCurrency(amount) {
  if (amount >= 100000) {
    return '₹' + (amount / 100000).toFixed(1) + 'L';
  }
  if (amount >= 1000) {
    return '₹' + (amount / 1000).toFixed(0) + 'K';
  }
  return '₹' + amount;
}
